import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { getPushMetrics, PushMetrics } from '../../services/adminService';

const CHANNEL_LABELS: Record<string, string> = {
  webpush: '웹푸시 (PWA·브라우저)',
  call: '화상통화 벨·정리',
  device: 'PC 앱 익명 알림',
  expo: '구 모바일 앱 (휴면)',
};

const ORDER = ['webpush', 'call', 'device', 'expo'];

/**
 * 푸시 발송 카운터 조회.
 * 서버 프로세스가 재시작되면 0으로 돌아간다 — 절대값이 아니라 "since 이후 누적"으로 읽어야 한다.
 */
export const PushMetricsPanel: React.FC = () => {
  const [data, setData] = useState<PushMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await getPushMetrics();
      setData(res);
      setError(null);
    } catch {
      setError('푸시 발송 현황을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  if (loading) {
    return <p className="text-sm text-slate-500">불러오는 중…</p>;
  }
  if (error) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-brand-700">{error}</p>
        <button
          type="button"
          onClick={load}
          className="px-3 py-2 bg-brand-600 text-white rounded-lg text-sm font-semibold hover:bg-brand-700 transition-colors"
        >
          다시 시도
        </button>
      </div>
    );
  }

  const channels = data?.channels || {};
  const failures = data?.recentFailures || [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          {data?.since ? `${new Date(data.since).toLocaleString('ko-KR')} 이후 누적` : ''} · 10초마다 갱신
        </p>
        <button
          type="button"
          onClick={load}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <RefreshCw size={13} aria-hidden="true" />
          새로고침
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3 font-semibold">경로</th>
              <th className="py-2 px-3 font-semibold text-right">시도</th>
              <th className="py-2 px-3 font-semibold text-right">성공</th>
              <th className="py-2 px-3 font-semibold text-right">실패</th>
              <th className="py-2 px-3 font-semibold text-right">재시도</th>
              <th className="py-2 px-3 font-semibold text-right">만료 정리</th>
              <th className="py-2 pl-3 font-semibold text-right">억제</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {ORDER.filter((k) => channels[k]).map((key) => {
              const c = channels[key];
              return (
                <tr key={key}>
                  <td className="py-2.5 pr-3 text-slate-800">{CHANNEL_LABELS[key] || key}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{c.attempted}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{c.sent}</td>
                  <td className={`py-2.5 px-3 text-right font-semibold ${c.failed > 0 ? 'text-brand-700' : 'text-slate-400'}`}>
                    {c.failed}
                  </td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{c.retried}</td>
                  <td className="py-2.5 px-3 text-right text-slate-600">{c.expired}</td>
                  <td className="py-2.5 pl-3 text-right text-slate-600">{c.suppressed}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">최근 실패</h3>
        {failures.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-6 text-center">
            <p className="text-sm text-slate-500">최근 실패한 발송이 없습니다</p>
          </div>
        ) : (
          <ul className="space-y-1.5">
            {failures
              .slice()
              .reverse()
              .map((f, i) => (
                <li key={`${f.at}-${i}`} className="text-xs text-slate-600 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-slate-400 tabular-nums">{new Date(f.at).toLocaleTimeString('ko-KR')}</span>
                  <span className="font-semibold text-slate-700">{CHANNEL_LABELS[f.channel] || f.channel}</span>
                  <span className="px-1.5 rounded bg-slate-100 text-slate-600">{f.status}</span>
                  {f.type ? <span className="text-slate-400">{f.type}</span> : null}
                  {f.origin ? <span className="text-slate-400">{f.origin}</span> : null}
                  <span className="text-slate-500 break-all">{f.message}</span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
};
